import { ReasonToBuyUI } from "./ReasonToBuyUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { TickCircleOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleOutlined";
import { DeliveryOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { GiftCardOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GiftCardOutlined";
import { GuaranteesAndTroubleshootingOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GuaranteesAndTroubleshootingOutlined";
import { ReturnsAndRefundsOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ReturnsAndRefundsOutlined";
import { pricingGroups } from "@/utils/product/getPromoMessaging";
import {
  EXCLUDED_CATEGORIES,
  EXCLUDED_PRODUCT_ID,
  EXCLUDED_PRODUCT_TYPE,
  EXCLUDED_BRANDS,
} from "@/utils/pricePromise/PricePromiseExclusions.ts";
import { ProtectPlusSvg } from "../PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg";
import { getScheme } from "../PostAddToBasketDrawer/scheme/schemeDecisioner";
import { replaceVariables } from "../PostAddToBasketDrawer/utils";
import { formatTimeToAmPm } from "@/utils/date/formatTime";
import { ReactNode, useMemo, useState } from "react";
import { PricePromiseDrawer } from "./PricePromise/PricePromiseDrawer";
import { useClickAndCollectCard } from "./utils/useClickAndCollectCard";
import {
  DMS_IDS,
  FREE_DELIVERY_THRESHOLD_DMS_IDS,
} from "@/constants/constants";
import {
  isProtectPlusExtendedWarranty,
  isIncludedGuarantee,
} from "./utils/utils";
import {
  isBeautyProductTypeHierarchy,
  isFashionProductTypeHierarchy,
  getProductTypeHierarchy,
  isHomeProductTypeHierarchy,
  isNurseryProductTypeHierarchy,
} from "./utils/productTypeHierarchy";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";
import { isEligibleForFreeReturns } from "@/utils/product/isEligibleForFreeReturns";
import { isTwoManDelivery } from "@/utils/product/isTwoManDelivery";
import { isMadeToMeasure } from "@/utils/product/isMadeToMeasure";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { getReturnsExclusionsMessage } from "./ReturnsExclusions/utils/getReturnsExclusionsMessage";
import { link } from "fs";

const LARGE_ELECTRICAL_INSTALLATION_RECYCLING_SELLING_PRODUCT_TYPES = [
  "Dishwashers",
  "Cookers",
  "Cooker Hoods",
  "Hobs",
  "Washer Dryers",
  "Fridge Freezers",
  "Ovens",
  "Fridges",
  "Freezers",
  "Washing Machines",
  "Tumble Dryers",
];

export type Card = {
  id: string;
  title: string;
  description?: string | undefined;
  descriptionHref?: string | undefined;
  onDescriptionClick?: () => void;
  icon?: ReactNode;
  linkUrl?: string | undefined;
  linkText?: string | undefined;
};

type CardProps = Card[];

export function ReasonToBuy({
  product,
  flags,
}: Pick<StandardProps, "product" | "flags">) {
  const { variant } = useVariant(product);
  const protectPlusScheme = getScheme(product);
  const [isPricePromiseDrawerOpen, setIsPricePromiseDrawerOpen] =
    useState(false);

  const isJohnLewisProduct =
    typeof product?.brand?.name === "string" &&
    ["john lewis", "anyday", "and/or", "kin"].some((brand) =>
      product?.brand?.name.toLowerCase().includes(brand),
    );

  const includedGuarantee = variant.services?.find(isIncludedGuarantee);
  const protectPlusItem =
    variant.services?.filter(isProtectPlusExtendedWarranty)?.[0] ?? null;

  const isPricePromiseEligible = useMemo(() => {
    const brandName = product?.brand?.name?.toLowerCase();

    const hasExclusiveAdditional = product?.messaging?.some(
      (msg) =>
        msg.type === "additional" &&
        typeof msg.title === "string" &&
        msg.title.toLowerCase().includes("exclusive"),
    );
    const productGroupHasPromos = product?.messaging?.some(
      (msg) =>
        "promotionGroup" in msg && pricingGroups.includes(msg.promotionGroup),
    );
    const categoryIsExcluded = product?.browsePageHierarchy?.some((page) =>
      EXCLUDED_CATEGORIES.includes(page.categoryId),
    );
    const brandIsExcluded =
      typeof brandName === "string" &&
      EXCLUDED_BRANDS.some((b) => brandName.includes(b));

    return !(
      hasExclusiveAdditional ||
      categoryIsExcluded ||
      brandIsExcluded ||
      product.aliases?.pimProductId === EXCLUDED_PRODUCT_ID ||
      product.__typename === EXCLUDED_PRODUCT_TYPE ||
      productGroupHasPromos
    );
  }, [product]);

  const clickAndCollectCard = useClickAndCollectCard(product);

  const shouldShowBeautyPlaceholderCard = useMemo(() => {
    return isBeautyProductTypeHierarchy(getProductTypeHierarchy(product));
  }, [product]);

  const shouldShowNurseryCard = useMemo(() => {
    return isNurseryProductTypeHierarchy(getProductTypeHierarchy(product));
  }, [product]);

  const shouldShowHomeCard = useMemo(() => {
    return isHomeProductTypeHierarchy(getProductTypeHierarchy(product));
  }, [product]);

  const shouldShowFashionCard = useMemo(() => {
    return isFashionProductTypeHierarchy(getProductTypeHierarchy(product));
  }, [product]);

  const protectPlusCard = useMemo(() => {
    if (!protectPlusItem) return null;

    const templateVars = {
      VAR_YEARS: protectPlusItem.duration ?? "",
      VAR_PRICE: protectPlusItem.price?.display ?? "",
    };

    const highlight = replaceVariables(
      protectPlusScheme?.schemeHighlight || "",
      templateVars,
    );
    const marketingMessage = replaceVariables(
      protectPlusScheme?.schemeMarketingMessage || "",
      templateVars,
    );

    return {
      id: "protect-plus",
      title:
        highlight ||
        `Add ${templateVars.VAR_YEARS} years of cover for accidental damage: ${templateVars.VAR_PRICE}`,
      description:
        marketingMessage ||
        `Protect your item from accidental damage for ${templateVars.VAR_YEARS} years, in addition to your product's standard guarantee.`,
      icon: <ProtectPlusSvg />,
    };
  }, [protectPlusItem, protectPlusScheme]);

  // Free returns card: shown for products genuinely eligible for unrestricted free
  // returns. Suppressed for 2-man delivery, made-to-measure, per-metre products,
  // selling-product-type/DMS-14-style returns exclusions (e.g. bras, beauty),
  // bladed-article restricted variants, and when the hideFreeReturnsMessage flag is set.
  const isFreeReturnsEligible = useMemo(() => {
    const isSellingProductTypeInExclusions = !!getReturnsExclusionsMessage(
      product.browsePageHierarchy,
      product.sellingProductType || "",
    );

    return (
      isEligibleForFreeReturns(product) &&
      !isTwoManDelivery(product) &&
      !isMadeToMeasure(product) &&
      !isPerMetre(product) &&
      !isSellingProductTypeInExclusions &&
      !variant.sellingRestrictions?.age?.bladedArticle &&
      flags?.hideFreeReturnsMessage?.value !== true
    );
  }, [product, variant.sellingRestrictions, flags]);

  const freeReturnsCard = useMemo(() => {
    if (!isFreeReturnsEligible) return null;

    return {
      id: "free-returns",
      title: "Free returns",
      // TODO: replace placeholder location count once a real figure is confirmed
      description:
        "Drop off at your local John Lewis or Waitrose store, or at thousands of other UK locations.",
      icon: (
        <img
          src="	https://static.johnlewiscontent.com/v1/static/Returns"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    };
  }, [isFreeReturnsEligible]);

  const installationAndRecyclingCard = useMemo(() => {
    const isEligibleSellingProductType =
      typeof product.sellingProductType === "string" &&
      LARGE_ELECTRICAL_INSTALLATION_RECYCLING_SELLING_PRODUCT_TYPES.some(
        (type) => type === product.sellingProductType,
      );
    const hasDeliveryService = variant.services?.some(
      (service) => service.__typename === "DeliveryService",
    );

    if (!isEligibleSellingProductType || !hasDeliveryService) {
      return null;
    }

    return {
      id: "installation-recycling",
      title: "Installation & recycling services available",
      description: "Learn more about our expert services",
      descriptionHref:
        "https://www.johnlewis.com/our-services/home-appliance-installation-and-disposal?source=pdprtb",
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/Installation"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    };
  }, [product.sellingProductType, variant.services]);

  const nextDayDeliveryOption = product.fulfilmentOptions?.available?.find(
    (option) => option.aliases?.dmsId === DMS_IDS.NextOrNamedDayDelivery,
  );
  const formattedOrderByTime = formatTimeToAmPm(
    nextDayDeliveryOption?.earliestFulfilment?.orderByLocalTimeToday,
  );

  const cards: CardProps = [
    clickAndCollectCard,
    includedGuarantee && {
      id: "guarantee",
      title: "Guaranteed for longer",
      linkText: "Read about our included guarantees",
      linkUrl:
        "https://www.johnlewis.com/customer-services/guarantees-and-troubleshooting-guides?source=pdprtb",
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/Guarantee-shield"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    },
    shouldShowBeautyPlaceholderCard && {
      id: "my-jl-beauty",
      title: "MY JL BEAUTY",
      description: "Be rewarded as part of the MyJL Beauty community",
      linkUrl: "/our-services/my-john-lewis-beauty?source=pdprtb",
      linkText: "Join MyJL Beauty",
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/MY-JL-BEAUTY-1"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    },
    shouldShowHomeCard && {
      id: "home",
      title:
        "Free in-store or virtual consultations with our home design stylists",
      description: "",
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/Home-design"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
      linkUrl: "/our-services/home-design-service?source=pdprtb",
      linkText: "Book a home design appointment",
    },
    shouldShowNurseryCard && {
      id: "nursery",
      title:
        "Get expert advice for your essential baby, toddler and child purchases",
      linkUrl:
        "https://www.johnlewis.com/our-services/nursery-advice?source=pdprtb",
      linkText: "Book a nursery appointment",
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/nursery"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    },
    shouldShowFashionCard && {
      id: "fashion",
      title: "Get a wardrobe refresh in-store with our stylists",
      description: "",
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/personal-styling"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
      linkText: "Book a personal styling appointment",
      linkUrl: "/our-services/personal-styling?source=pdprtb",
    },
    freeReturnsCard,
    isPricePromiseEligible && {
      id: "price-promise",
      title: "Our price promise",
      description: "Confidence in every purchase",
      onDescriptionClick: () => {
        setIsPricePromiseDrawerOpen(true);
        sendEvent("WPDP | Price Promise Info Displayed");
      },
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/NKU-icon"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    },
    protectPlusCard,
  ].filter((card): card is Card => !!card);

  if (formattedOrderByTime && nextDayDeliveryOption) {
    cards.push({
      id: "next-day-delivery",
      title: "Next day delivery available",
      description: `Order by ${formattedOrderByTime} to receive by the next day. ${nextDayDeliveryOption.price.display}`,
      icon: (
        <img
          src="https://static.johnlewiscontent.com/v1/static/Next-day-deliv"
          alt=""
          width={54}
          height={54}
          style={{ display: "block" }}
        />
      ),
    });
  }

  if (installationAndRecyclingCard) {
    cards.push(installationAndRecyclingCard);
  }

  if (!cards.length) return null;

  return (
    <>
      <ReasonToBuyUI cards={cards} />
      {isPricePromiseEligible && (
        <PricePromiseDrawer
          product={product}
          isOpen={isPricePromiseDrawerOpen}
          onClose={() => setIsPricePromiseDrawerOpen(false)}
          isJohnLewisProduct={isJohnLewisProduct}
        />
      )}
    </>
  );
}
